import React, { useState, useMemo, useRef, useEffect } from 'react'
import {
  IoCalendarOutline,
  IoBookmarkOutline,
  IoFilterOutline,
  IoChevronDown,
  IoChevronUp,
  IoWarningOutline,
  IoFlameOutline,
  IoTrophyOutline,
  IoArrowDownOutline,
  IoChevronForward,
} from 'react-icons/io5'
import { useCardsOverview } from '../../swr-config'
import type { FixtureCardSummary, CardsOverviewResponse } from '../../types'
import { ConfidenceIndicator } from './components/ConfidenceIndicator'
import { ErrorState } from '../ErrorState'
import { fmtKickoff } from '../../utils'

interface CardsOverviewProps {
  date?: string | null
  onDateChange: (date: string) => void
  onFixtureSelect: (fixtureId: number) => void
  onViewSaved: () => void
}

type SortOption = 'kickoff' | 'projection' | 'confidence' | 'value'
type SortDirection = 'asc' | 'desc'
type StatusFilter = 'all' | 'upcoming' | 'completed'
type ProjectionFilter = 'all' | 'high' | 'medium' | 'low'

export function CardsOverview({ date, onDateChange, onFixtureSelect, onViewSaved }: CardsOverviewProps) {
  const [sortBy, setSortBy] = useState<SortOption>('kickoff')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [minConfidence, setMinConfidence] = useState<number | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [projectionFilter, setProjectionFilter] = useState<ProjectionFilter>('all')
  const [selectedLeagues, setSelectedLeagues] = useState<Set<number>>(new Set())
  const [showLeagueDropdown, setShowLeagueDropdown] = useState(false)
  const leagueDropdownRef = useRef<HTMLDivElement>(null)

  // Format today's date as YYYY-MM-DD
  const today = useMemo(() => {
    const d = new Date()
    return d.toISOString().split('T')[0]
  }, [])

  const effectiveDate = date || today

  const { data, error, isLoading, mutate } = useCardsOverview({
    date: effectiveDate,
    leagueId: null,
    sort: 'kickoff', // Always get kickoff sort from API, handle other sorts client-side
    minConfidence,
  })

  // Derive available leagues from fixture data
  const availableLeagues = useMemo(() => {
    if (!data?.fixtures) return []
    const seen = new Map<number, string>()
    data.fixtures.forEach((f: FixtureCardSummary) => {
      if (!seen.has(f.competition_id)) {
        seen.set(f.competition_id, f.competition_name)
      }
    })
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [data?.fixtures])

  // Close league dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (leagueDropdownRef.current && !leagueDropdownRef.current.contains(e.target as Node)) {
        setShowLeagueDropdown(false)
      }
    }
    if (showLeagueDropdown) {
      document.addEventListener('mousedown', handleClick)
      return () => document.removeEventListener('mousedown', handleClick)
    }
  }, [showLeagueDropdown])

  const toggleLeague = (id: number) => {
    setSelectedLeagues((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAllLeagues = () => setSelectedLeagues(new Set())
  const clearAllLeagues = () => setSelectedLeagues(new Set())

  // Filter fixtures
  const filteredFixtures = useMemo(() => {
    if (!data?.fixtures) return []

    return data.fixtures.filter((fixture: FixtureCardSummary) => {
      // League filter (empty set = all leagues)
      if (selectedLeagues.size > 0 && !selectedLeagues.has(fixture.competition_id)) return false

      // Status filter
      if (statusFilter === 'upcoming' && fixture.status !== 'NS') return false
      if (statusFilter === 'completed' && fixture.status !== 'FT') return false

      // Projection range filter
      const projected = fixture.projected_total_cards
      if (projectionFilter === 'high' && (projected == null || projected < 4.5)) return false
      if (projectionFilter === 'medium' && (projected == null || projected < 3.0 || projected >= 4.5)) return false
      if (projectionFilter === 'low' && (projected == null || projected >= 3.0)) return false

      return true
    })
  }, [data?.fixtures, statusFilter, projectionFilter, selectedLeagues])

  // Sort fixtures
  const sortedFixtures = useMemo(() => {
    const sorted = [...filteredFixtures]

    sorted.sort((a, b) => {
      let comparison = 0

      switch (sortBy) {
        case 'kickoff':
          comparison = new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime()
          break
        case 'projection':
          const projA = a.projected_total_cards ?? -1
          const projB = b.projected_total_cards ?? -1
          comparison = projB - projA // Default descending for projection
          break
        case 'confidence':
          const confA = a.confidence_score ?? -1
          const confB = b.confidence_score ?? -1
          comparison = confB - confA // Default descending for confidence
          break
        case 'value':
          // Value = distance from 4.5 line
          const edgeA = Math.abs((a.projected_total_cards ?? 4.5) - 4.5)
          const edgeB = Math.abs((b.projected_total_cards ?? 4.5) - 4.5)
          comparison = edgeB - edgeA // Default descending for value
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return sorted
  }, [filteredFixtures, sortBy, sortDirection])

  // Handle column header click for sorting
  const handleSort = (column: SortOption) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      // Default directions based on column type
      setSortDirection(column === 'kickoff' ? 'asc' : 'desc')
    }
  }

  // Date navigation
  const handlePrevDay = () => {
    const d = new Date(effectiveDate)
    d.setDate(d.getDate() - 1)
    onDateChange(d.toISOString().split('T')[0])
  }

  const handleNextDay = () => {
    const d = new Date(effectiveDate)
    d.setDate(d.getDate() + 1)
    onDateChange(d.toISOString().split('T')[0])
  }

  const handleToday = () => {
    onDateChange(today)
  }

  // Format display date
  const formatDisplayDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00')
    const isToday = dateStr === today
    const dayName = d.toLocaleDateString('en-US', { weekday: 'short' })
    const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    return isToday ? `Today, ${monthDay}` : `${dayName}, ${monthDay}`
  }

  // Get sort indicator
  const getSortIndicator = (column: SortOption) => {
    if (sortBy !== column) return null
    return sortDirection === 'asc' ? '↑' : '↓'
  }

  if (isLoading) {
    return (
      <div className="cards-overview">
        <div className="cards-overview-header">
          <h1 className="cards-overview-title">Cards Hub</h1>
        </div>
        <div className="cards-loading">
          <div className="loading-spinner" />
          <p>Loading card predictions...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="cards-overview">
        <div className="cards-overview-header">
          <h1 className="cards-overview-title">Cards Hub</h1>
        </div>
        <ErrorState
          title="Failed to load predictions"
          message={error.message}
          onRetry={() => mutate()}
        />
      </div>
    )
  }

  return (
    <div className="cards-overview">
      {/* Header */}
      <div className="cards-overview-header">
        <div className="cards-overview-header-top">
          <h1 className="cards-overview-title">Cards Hub</h1>
          <button
            type="button"
            className="cards-saved-btn"
            onClick={onViewSaved}
            aria-label="View saved fixtures"
          >
            <IoBookmarkOutline size={18} />
            <span>Saved</span>
          </button>
        </div>

        {/* Date Picker */}
        <div className="cards-date-picker">
          <button
            type="button"
            className="cards-date-nav-btn"
            onClick={handlePrevDay}
            aria-label="Previous day"
          >
            <IoChevronDown size={18} style={{ transform: 'rotate(90deg)' }} />
          </button>
          <button
            type="button"
            className="cards-date-display"
            onClick={handleToday}
          >
            <IoCalendarOutline size={16} />
            <span>{formatDisplayDate(effectiveDate)}</span>
          </button>
          <button
            type="button"
            className="cards-date-nav-btn"
            onClick={handleNextDay}
            aria-label="Next day"
          >
            <IoChevronDown size={18} style={{ transform: 'rotate(-90deg)' }} />
          </button>
        </div>

        {/* League Dropdown */}
        {availableLeagues.length > 1 && (
          <div className="cards-league-dropdown" ref={leagueDropdownRef}>
            <button
              type="button"
              className={`cards-league-toggle ${showLeagueDropdown ? 'active' : ''}`}
              onClick={() => setShowLeagueDropdown(!showLeagueDropdown)}
            >
              <span>
                {selectedLeagues.size === 0
                  ? 'All Leagues'
                  : selectedLeagues.size === 1
                    ? availableLeagues.find((l) => selectedLeagues.has(l.id))?.name ?? 'All Leagues'
                    : `${selectedLeagues.size} Leagues`}
              </span>
              <IoChevronDown size={14} style={{ transform: showLeagueDropdown ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s ease' }} />
            </button>
            {showLeagueDropdown && (
              <div className="cards-league-menu">
                <button
                  type="button"
                  className={`cards-league-option ${selectedLeagues.size === 0 ? 'selected' : ''}`}
                  onClick={selectAllLeagues}
                >
                  <span className="cards-league-check">{selectedLeagues.size === 0 ? '✓' : ''}</span>
                  All Leagues
                </button>
                <div className="cards-league-divider" />
                {availableLeagues.map((league) => (
                  <button
                    key={league.id}
                    type="button"
                    className={`cards-league-option ${selectedLeagues.has(league.id) ? 'selected' : ''}`}
                    onClick={() => toggleLeague(league.id)}
                  >
                    <span className="cards-league-check">{selectedLeagues.has(league.id) ? '✓' : ''}</span>
                    {league.name}
                  </button>
                ))}
                {selectedLeagues.size > 0 && (
                  <>
                    <div className="cards-league-divider" />
                    <button
                      type="button"
                      className="cards-league-option cards-league-clear"
                      onClick={clearAllLeagues}
                    >
                      Clear All
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Filters Toggle */}
        <div className="cards-filters-section">
          <button
            type="button"
            className={`cards-filters-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <IoFilterOutline size={16} />
            <span>Filters</span>
            {showFilters ? <IoChevronUp size={14} /> : <IoChevronDown size={14} />}
          </button>

          {showFilters && (
            <div className="cards-filters-panel">
              <div className="cards-filter-group">
                <label className="cards-filter-label">Min Confidence</label>
                <div className="cards-filter-chips">
                  <button
                    type="button"
                    className={`chip ${minConfidence === null ? 'chip-active' : ''}`}
                    onClick={() => setMinConfidence(null)}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`chip ${minConfidence === 0.5 ? 'chip-active' : ''}`}
                    onClick={() => setMinConfidence(0.5)}
                  >
                    50%+
                  </button>
                  <button
                    type="button"
                    className={`chip ${minConfidence === 0.7 ? 'chip-active' : ''}`}
                    onClick={() => setMinConfidence(0.7)}
                  >
                    70%+
                  </button>
                </div>
              </div>

              <div className="cards-filter-group">
                <label className="cards-filter-label">Fixture Status</label>
                <div className="cards-filter-chips">
                  <button
                    type="button"
                    className={`chip ${statusFilter === 'all' ? 'chip-active' : ''}`}
                    onClick={() => setStatusFilter('all')}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`chip ${statusFilter === 'upcoming' ? 'chip-active' : ''}`}
                    onClick={() => setStatusFilter('upcoming')}
                  >
                    Upcoming
                  </button>
                  <button
                    type="button"
                    className={`chip ${statusFilter === 'completed' ? 'chip-active' : ''}`}
                    onClick={() => setStatusFilter('completed')}
                  >
                    Completed
                  </button>
                </div>
              </div>

              <div className="cards-filter-group">
                <label className="cards-filter-label">Card Projection</label>
                <div className="cards-filter-chips">
                  <button
                    type="button"
                    className={`chip ${projectionFilter === 'all' ? 'chip-active' : ''}`}
                    onClick={() => setProjectionFilter('all')}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`chip ${projectionFilter === 'high' ? 'chip-active' : ''}`}
                    onClick={() => setProjectionFilter('high')}
                  >
                    High (4.5+)
                  </button>
                  <button
                    type="button"
                    className={`chip ${projectionFilter === 'medium' ? 'chip-active' : ''}`}
                    onClick={() => setProjectionFilter('medium')}
                  >
                    Medium (3-4.5)
                  </button>
                  <button
                    type="button"
                    className={`chip ${projectionFilter === 'low' ? 'chip-active' : ''}`}
                    onClick={() => setProjectionFilter('low')}
                  >
                    Low (&lt;3)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        {data && (
          <div className="cards-overview-stats">
            <div className="cards-stat">
              <span className="cards-stat-value">{sortedFixtures.length}</span>
              <span className="cards-stat-label">Showing</span>
            </div>
            <div className="cards-stat">
              <span className="cards-stat-value">{data.fixtures_with_predictions}</span>
              <span className="cards-stat-label">With Predictions</span>
            </div>
            {data.average_projected_cards && (
              <div className="cards-stat">
                <span className="cards-stat-value">{data.average_projected_cards.toFixed(1)}</span>
                <span className="cards-stat-label">Avg Cards</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filter status indicator */}
      {(statusFilter !== 'all' || projectionFilter !== 'all' || selectedLeagues.size > 0) && sortedFixtures.length !== data?.fixtures.length && (
        <div className="cards-filter-status">
          <IoFilterOutline size={14} />
          <span>
            Showing {sortedFixtures.length} of {data?.fixtures.length} fixtures
            {statusFilter !== 'all' && ` • ${statusFilter}`}
            {projectionFilter !== 'all' && ` • ${projectionFilter} cards`}
            {selectedLeagues.size > 0 && ` • ${selectedLeagues.size} league${selectedLeagues.size > 1 ? 's' : ''}`}
          </span>
          <button
            type="button"
            className="cards-clear-filters-btn"
            onClick={() => {
              setStatusFilter('all')
              setProjectionFilter('all')
              setSelectedLeagues(new Set())
            }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      {sortedFixtures.length === 0 ? (
        <div className="cards-empty-state">
          <IoWarningOutline size={48} />
          <h3>No fixtures found</h3>
          <p>
            {statusFilter !== 'all' || projectionFilter !== 'all' || selectedLeagues.size > 0
              ? 'No fixtures match your current filters. Try adjusting them.'
              : 'There are no fixtures with card predictions for this date.'}
          </p>
          {(statusFilter !== 'all' || projectionFilter !== 'all' || selectedLeagues.size > 0) && (
            <button
              type="button"
              className="cards-clear-filter-btn"
              onClick={() => {
                setStatusFilter('all')
                setProjectionFilter('all')
                setSelectedLeagues(new Set())
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="cards-table-container">
          <table className="cards-table">
            <thead>
              <tr>
                <th
                  className={`cards-th cards-th-sortable ${sortBy === 'kickoff' ? 'cards-th-active' : ''}`}
                  onClick={() => handleSort('kickoff')}
                >
                  Time {getSortIndicator('kickoff')}
                </th>
                <th className="cards-th">Match</th>
                <th className="cards-th cards-th-competition">Competition</th>
                <th className="cards-th cards-th-referee">Referee</th>
                <th
                  className={`cards-th cards-th-sortable cards-th-center ${sortBy === 'projection' ? 'cards-th-active' : ''}`}
                  onClick={() => handleSort('projection')}
                >
                  Cards {getSortIndicator('projection')}
                </th>
                <th
                  className={`cards-th cards-th-sortable cards-th-center ${sortBy === 'confidence' ? 'cards-th-active' : ''}`}
                  onClick={() => handleSort('confidence')}
                >
                  Confidence {getSortIndicator('confidence')}
                </th>
                <th className="cards-th cards-th-context">Context</th>
                <th className="cards-th cards-th-action"></th>
              </tr>
            </thead>
            <tbody>
              {sortedFixtures.map((fixture) => (
                <CardsTableRow
                  key={fixture.fixture_id}
                  fixture={fixture}
                  onSelect={() => onFixtureSelect(fixture.fixture_id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Table row component
interface CardsTableRowProps {
  fixture: FixtureCardSummary
  onSelect: () => void
}

function CardsTableRow({ fixture, onSelect }: CardsTableRowProps) {
  const hasProjection = fixture.projected_total_cards != null
  const isHighCard = hasProjection && fixture.projected_total_cards! >= 4.5
  const kickoffTime = fmtKickoff(fixture.kickoff)

  const hasContext = fixture.is_derby || fixture.is_title_race || fixture.is_relegation_battle

  return (
    <tr
      className={`cards-tr ${isHighCard ? 'cards-tr-high' : ''}`}
      onClick={onSelect}
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onSelect()}
      role="button"
      aria-label={`${fixture.home_team_name} vs ${fixture.away_team_name}`}
    >
      {/* Time */}
      <td className="cards-td cards-td-time">
        <span className="cards-time">{kickoffTime}</span>
      </td>

      {/* Match */}
      <td className="cards-td cards-td-match">
        <div className="cards-match">
          <div className="cards-match-team">
            {fixture.home_team_logo && (
              <img src={fixture.home_team_logo} alt="" className="cards-team-logo" loading="lazy" />
            )}
            <span className="cards-team-name">{fixture.home_team_name}</span>
          </div>
          <span className="cards-match-vs">v</span>
          <div className="cards-match-team">
            {fixture.away_team_logo && (
              <img src={fixture.away_team_logo} alt="" className="cards-team-logo" loading="lazy" />
            )}
            <span className="cards-team-name">{fixture.away_team_name}</span>
          </div>
        </div>
      </td>

      {/* Competition */}
      <td className="cards-td cards-td-competition">
        <span className="cards-competition">{fixture.competition_name}</span>
      </td>

      {/* Referee */}
      <td className="cards-td cards-td-referee">
        {fixture.referee_name ? (
          <span className="cards-referee">
            {fixture.referee_strictness_label && (
              <span
                className={`cards-referee-dot cards-referee-dot-${fixture.referee_strictness_label}`}
                title={
                  fixture.referee_strictness_label === 'strict'
                    ? 'Strict referee (above avg cards)'
                    : fixture.referee_strictness_label === 'lenient'
                      ? 'Lenient referee (below avg cards)'
                      : 'Average referee'
                }
              />
            )}
            {fixture.referee_name}
          </span>
        ) : (
          <span className="cards-referee cards-referee-tbd">TBD</span>
        )}
      </td>

      {/* Projected Cards */}
      <td className="cards-td cards-td-projection">
        {hasProjection ? (
          <span className={`cards-projection ${isHighCard ? 'cards-projection-high' : ''}`}>
            {fixture.projected_total_cards!.toFixed(1)}
          </span>
        ) : (
          <span className="cards-projection cards-projection-na">—</span>
        )}
      </td>

      {/* Confidence */}
      <td className="cards-td cards-td-confidence">
        {fixture.confidence_score != null && fixture.confidence_level ? (
          <ConfidenceIndicator
            score={fixture.confidence_score}
            level={fixture.confidence_level}
            compact
          />
        ) : (
          <span className="cards-confidence-na">—</span>
        )}
      </td>

      {/* Context */}
      <td className="cards-td cards-td-context">
        {hasContext ? (
          <div className="cards-context-badges">
            {fixture.is_derby && (
              <span className="cards-badge cards-badge-derby" title="Derby">
                <IoFlameOutline size={12} />
              </span>
            )}
            {fixture.is_title_race && (
              <span className="cards-badge cards-badge-title" title="Title Race">
                <IoTrophyOutline size={12} />
              </span>
            )}
            {fixture.is_relegation_battle && (
              <span className="cards-badge cards-badge-relegation" title="Relegation">
                <IoArrowDownOutline size={12} />
              </span>
            )}
          </div>
        ) : null}
      </td>

      {/* Action */}
      <td className="cards-td cards-td-action">
        <IoChevronForward size={16} className="cards-row-chevron" />
      </td>
    </tr>
  )
}
